import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { MultiColumnVizProps } from './types';
import { dsVar } from '../../theme/tokens';
import { tableCellPadSx, tableCellTypeSx, tableHeadTypeSx } from '../../theme/dataTable';

// Past this many characters a column holds prose (a rationale, a list of signals)
// and is given room to read as lines rather than a one-word-wide stack.
const LONG_TEXT_CHARS = 60;

const cellValue = (row: any, header: string, index: number): any =>
  Array.isArray(row)
    ? row[index]
    : row[header] ||
      row[header.toLowerCase()] ||
      row[header.toLowerCase().replace(/ /g, '_')];

/**
 * Renders a standard multi-column MUI table.
 * Used as the default fallback when no specific visualizationType is set.
 *
 * Columns hold a minimum width and the table scrolls sideways, so a wide table
 * in a narrow drawer stays readable instead of wrapping every cell to one word.
 */
const MultiColumnTable: React.FC<MultiColumnVizProps> = ({
  headers,
  data,
  safeValue,
}) => {
  const longColumns = headers.map((header, idx) =>
    data.some((row) => {
      const value = cellValue(row, header, idx);
      return typeof value === 'string' && value.length > LONG_TEXT_CHARS;
    })
  );

  return (
    <div className="mb-8 overflow-x-auto">
      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: dsVar('surface'),
          border: `1px solid ${dsVar('line')}`,
          borderRadius: '12px',
          boxShadow: 'none',
        }}
      >
        <Table size="small">
          <TableHead sx={{ backgroundColor: dsVar('raised') }}>
            <TableRow>
              {headers.map((header, idx) => (
                <TableCell
                  key={idx}
                  sx={{
                    ...tableHeadTypeSx,
                    ...tableCellPadSx,
                    whiteSpace: 'nowrap',
                    borderBottom: `1px solid ${dsVar('line')}`,
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, rowIdx) => (
              <TableRow
                key={rowIdx}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                {headers.map((header, cellIdx) => (
                  <TableCell
                    key={cellIdx}
                    sx={{
                      ...tableCellTypeSx,
                      ...tableCellPadSx,
                      verticalAlign: 'top',
                      minWidth: longColumns[cellIdx] ? 240 : 96,
                      borderBottom: `1px solid ${dsVar('line')}`,
                    }}
                  >
                    {safeValue(cellValue(row, header, cellIdx))}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default MultiColumnTable;
